import { removeMetadataFromComment } from '@/lib/ai/code-review-service/utils/metadata';
import { sanitizePromptContent } from '@/lib/security/prompt-sanitization';

export type CommentContextOptions = {
  comment: string;
  filename?: string;
  lineNumber?: number;
  fileContent?: string;
  diffHunk?: string;
  style?: 'verbose' | 'minimal';
};

/**
 * Number of lines we include when generating code context snippets for AI.
 * We keep this small (5) to provide just enough locality around the target line
 * while avoiding oversized prompt payloads.
 */
const AI_CONTEXT_LINES = 5;

function getContextLines(fileContent: string, lineNumber: number): string {
  const lines = fileContent.split('\n');
  const start = Math.max(0, lineNumber - 1 - Math.floor(AI_CONTEXT_LINES / 2));
  const end = Math.min(lines.length, start + AI_CONTEXT_LINES);
  return lines.slice(start, end).join('\n');
}

function buildHeader(filename?: string, lineNumber?: number): string {
  let header = 'Validate whether the following comment';
  if (filename) {
    header += ` on ${filename}`;
  }
  if (lineNumber !== undefined) {
    header += ` at line ${lineNumber}`;
  }
  return `${header} is true, and if so, address it:\n\n`;
}

function buildFileContext(params: {
  fileContent?: string;
  diffHunk?: string;
  lineNumber?: number;
}): string {
  const { fileContent, diffHunk, lineNumber } = params;

  if (diffHunk) {
    return `<file context>${diffHunk}</file context>`;
  }

  if (fileContent && lineNumber !== undefined) {
    const context = getContextLines(fileContent, lineNumber);
    return `<file context>${context}</file context>`;
  }

  if (fileContent) {
    return `<file context>${fileContent
      .split('\n')
      .slice(0, AI_CONTEXT_LINES * 2)
      .join('\n')}</file context>`;
  }

  return '';
}

export function buildCommentContext(options: CommentContextOptions): string {
  const { comment, filename, lineNumber, fileContent, diffHunk, style = 'minimal' } = options;

  const cleanComment = sanitizePromptContent(removeMetadataFromComment(comment)) ?? '';

  if (style === 'verbose') {
    const header = buildHeader(filename, lineNumber);
    const commentXml = `<comment>${cleanComment}</comment>`;
    const fileContextXml = buildFileContext({ fileContent, diffHunk, lineNumber });

    return `${header}${commentXml}\n\n${fileContextXml}`.trim();
  }

  const fileContext = buildFileContext({ fileContent, diffHunk, lineNumber });
  const diffContext = fileContext ? `\n\n${fileContext}` : '';
  return `${cleanComment}${diffContext}`;
}
